"use client"

import { format } from "date-fns"
import { CalendarIcon, X, Loader2 } from "lucide-react"
import { useState, useEffect, useCallback, useRef } from "react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

interface DatePickerProps {
  value?: Date | string | null
  onChange?: (date: Date | undefined) => void
  /** Async save handler - if provided, will show loading spinner during save */
  onSave?: (date: Date | undefined) => Promise<void>
  placeholder?: string
  className?: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
  disabled?: boolean
  "aria-label"?: string
}

export function DatePicker({
  value,
  onChange,
  onSave,
  placeholder = "Pick a date",
  className,
  open,
  onOpenChange,
  disabled = false,
  "aria-label": ariaLabel,
}: DatePickerProps) {
  const parseDateAsLocal = (dateString: string): Date => {
    const parts = dateString.split('-').map(Number);
    if (parts.length !== 3 || parts.some(isNaN)) {
      return new Date();
    }
    const [year, month, day] = parts as [number, number, number];
    return new Date(year, month - 1, day);
  };

  // Parse typed date input (supports MM/DD/YYYY, M/D/YYYY, MM-DD-YYYY, etc.)
  const parseTypedDate = (input: string): Date | null => {
    if (!input || input.trim() === '') return null;

    // Remove extra spaces
    const cleaned = input.trim();

    // Try parsing common formats: MM/DD/YYYY, M/D/YYYY, MM-DD-YYYY, M-D-YYYY
    const slashMatch = cleaned.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
    if (slashMatch && slashMatch[1] && slashMatch[2] && slashMatch[3]) {
      const month = parseInt(slashMatch[1], 10);
      const day = parseInt(slashMatch[2], 10);
      let year = parseInt(slashMatch[3], 10);

      // Handle 2-digit years (assume 2000s)
      if (year < 100) {
        year += 2000;
      }

      // Validate ranges
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 1900 && year <= 2100) {
        return new Date(year, month - 1, day);
      }
    }

    return null;
  };

  const dateValue = typeof value === 'string' ? parseDateAsLocal(value) : value;

  // Local state for the input field
  const [inputValue, setInputValue] = useState(dateValue ? format(dateValue, "MM/dd/yyyy") : "");
  const [isFocused, setIsFocused] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [internalOpen, setInternalOpen] = useState(false);

  // Ref to track in-flight saves (prevents race conditions)
  const saveInProgressRef = useRef(false);
  // Ref to queue a date pick that arrives during an in-flight save
  const pendingDateRef = useRef<{ date: Date | undefined } | null>(null);

  // Use controlled open state if provided, otherwise use internal state
  const isOpen = open !== undefined ? open : internalOpen;
  const setIsOpen = onOpenChange || setInternalOpen;

  // Update input value when the date prop changes (e.g., from calendar selection)
  // But only if the input is not currently focused (don't interrupt typing)
  // and not currently saving (don't override optimistic update)
  useEffect(() => {
    if (!isFocused && !isSaving) {
      setInputValue(dateValue ? format(dateValue, "MM/dd/yyyy") : "");
    }
  }, [value, dateValue, isFocused, isSaving]);

  const handleChange = useCallback(async (date: Date | undefined) => {
    // Close the popover when a date is selected
    setIsOpen(false);

    // If a save is already in progress, queue instead of dropping
    if (saveInProgressRef.current) {
      pendingDateRef.current = { date };
      setInputValue(date ? format(date, "MM/dd/yyyy") : "");
      return;
    }

    if (onSave) {
      const previousInputValue = inputValue;
      // Optimistic update
      setInputValue(date ? format(date, "MM/dd/yyyy") : "");
      setIsSaving(true);
      saveInProgressRef.current = true;
      try {
        await onSave(date);
      } catch (error) {
        // Rollback on error
        setInputValue(previousInputValue);
        throw error;
      } finally {
        setIsSaving(false);
        saveInProgressRef.current = false;
        // Process queued date change
        if (pendingDateRef.current) {
          const pending = pendingDateRef.current;
          pendingDateRef.current = null;
          handleChange(pending.date);
        }
      }
    } else if (onChange) {
      onChange(date);
    }
  }, [onSave, onChange, inputValue, setIsOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    // Remove all non-numeric characters except slashes
    const numbers = value.replace(/[^\d]/g, '');

    // Apply mask: MM/DD/YYYY
    if (numbers.length <= 2) {
      // Just month digits
      value = numbers;
    } else if (numbers.length <= 4) {
      // Month + day digits
      value = numbers.slice(0, 2) + '/' + numbers.slice(2);
    } else {
      // Month + day + year digits
      value = numbers.slice(0, 2) + '/' + numbers.slice(2, 4) + '/' + numbers.slice(4, 8);
    }

    setInputValue(value);
  };

  const handleInputFocus = () => {
    setIsFocused(true);
  };

  const handleInputBlur = () => {
    setIsFocused(false);
    const parsedDate = parseTypedDate(inputValue);
    if (parsedDate) {
      // Only save if the date actually changed
      if (!dateValue || parsedDate.getTime() !== dateValue.getTime()) {
        handleChange(parsedDate);
      }
    } else if (inputValue.trim() === '') {
      // Clear the date only if there was a previous value
      if (dateValue) {
        handleChange(undefined);
      }
    } else {
      // Reset to the current value if parsing failed
      setInputValue(dateValue ? format(dateValue, "MM/dd/yyyy") : "");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const parsedDate = parseTypedDate(inputValue);
      if (parsedDate) {
        handleChange(parsedDate);
      }
    }
  };

  return (
    <div className="relative flex items-center">
      <Input
        type="text"
        aria-label={ariaLabel}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={isSaving || disabled}
        className={cn(dateValue ? "pr-20" : "pr-10", isSaving && "pr-24", className)}
      />
      <div className="absolute right-1 flex items-center gap-0.5">
        {isSaving && (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mr-1" />
        )}
        <button
          onClick={(e) => {
            e.stopPropagation()
            handleChange(undefined)
            setInputValue("")
          }}
          className="p-1.5 hover:bg-gray-100 rounded-sm transition-colors z-10"
          aria-label="Clear date"
          style={{ display: dateValue && !isSaving && !disabled ? 'block' : 'none' }}
          disabled={isSaving || disabled}
        >
          <X className="h-4 w-4 text-gray-500" />
        </button>
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              type="button"
              disabled={isSaving || disabled}
            >
              <CalendarIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="single"
              selected={dateValue || undefined}
              onSelect={handleChange}
              defaultMonth={dateValue || undefined}
              fixedWeeks
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}
